import { useRef } from 'react';
import { IntlProvider } from 'react-intl';
import { Outlet, useMatch } from 'react-router';
import { SessionExpiredDialog } from '@rio-cloud/rio-session-expired-info';
import ApplicationLayout from '@rio-cloud/rio-uikit/lib/es/ApplicationLayout';
import NotificationsContainer from '@rio-cloud/rio-uikit/lib/es/NotificationsContainer';

import { DEFAULT_LOCALE } from '@/configuration/lang/lang';
import { getDisplayMessages, getLocale } from '@/configuration/lang/langSlice';
import { isUserSessionExpired } from '@/configuration/login/loginSlice';
import { useAppDispatch, useAppSelector } from '@/configuration/setup/hooks';
import { getSessionExpiredAcknowledged, hideSessionExpiredDialog } from '@/data/appSlice';
import AppHeader from '@/features/app/header/AppHeader';
import Info from '@/features/app/header/info/Info';
import SettingsDialog from '@/features/app/header/settings/SettingsDialog';
import { useStoredReleaseNotes } from '@/features/app/header/useStoredReleaseNotes';
import MfeBottomSheet from '@/features/microfrontends/MfeBottomSheet';
import MfeDialog from '@/features/microfrontends/MfeDialog';
import MfeMessageHandler from '@/features/microfrontends/MfeMessageHandler';
import MfePopover from '@/features/microfrontends/MfePopover';
import SystemMaintenanceBanner from '@/features/systemMaintenance/SystemMaintenanceBanner';
import { isMaintenanceAnnounced } from '@/features/systemMaintenance/systemMaintenance';
import DefaultRedirect from '@/routes/DefaultRedirect';
import { CHECKOUT_ROUTE } from '@/routes/Router';
import { AppContext } from './AppContext';

const AppLayout = () => {
    const { setLastSeenToNow } = useStoredReleaseNotes();
    const sidebarRef = useRef<HTMLDivElement>(null);
    const treeRef = useRef<HTMLDivElement>(null);
    const dispatch = useAppDispatch();

    const userLocale = useAppSelector(getLocale);
    const displayMessages = useAppSelector(getDisplayMessages);
    const isSessionExpired = useAppSelector(isUserSessionExpired);
    const sessionExpiredAcknowledged = useAppSelector(getSessionExpiredAcknowledged);

    const hideIfCheckoutRoute = useMatch(CHECKOUT_ROUTE) !== null ? 'display-none' : '';

    if (!(displayMessages && userLocale)) {
        return null;
    }

    const handleSessionExpiredDialogClose = () => dispatch(hideSessionExpiredDialog);
    const showSessionExpired = isSessionExpired && !sessionExpiredAcknowledged;

    return (
        <IntlProvider defaultLocale={DEFAULT_LOCALE} key={userLocale} locale={userLocale} messages={displayMessages}>
            <AppContext.Provider value={{ sidebarRef, treeRef }}>
                {isMaintenanceAnnounced() && <SystemMaintenanceBanner />}
                <ApplicationLayout>
                    <ApplicationLayout.Header className={hideIfCheckoutRoute}>
                        <AppHeader />
                    </ApplicationLayout.Header>
                    <ApplicationLayout.Sidebar ref={treeRef} />
                    <ApplicationLayout.Sidebar className="right" ref={sidebarRef} />
                    <ApplicationLayout.Body className="responsive">
                        <Info setLastSeenToNow={setLastSeenToNow} />
                        <SettingsDialog />
                        <MfeDialog />
                        <NotificationsContainer />
                        <SessionExpiredDialog locale={userLocale} onClose={handleSessionExpiredDialogClose} show={showSessionExpired} />
                        <MfeMessageHandler />
                        <MfeBottomSheet />
                        <MfePopover />
                        <Outlet />
                        <DefaultRedirect />
                    </ApplicationLayout.Body>
                </ApplicationLayout>
            </AppContext.Provider>
        </IntlProvider>
    );
};

export default AppLayout;
